import React, { useEffect, useState } from 'react';
import '../../styles/CometChatLogin/CometChatLogin.css';
import ClientPortal from '../../assets/LABOR-LAW-PC-LOGO_Colored-2-6ca53189.png';
import HeroImage from '../../assets/danny_home_2-scaled-5c81f8af-e9499222.png';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import OtpInput from '../OtpInput/OtpInput';
import { Link, useLocation } from 'react-router-dom';

type Step = 'email' | 'otp';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
console.log("API_BASE_URL->", API_BASE_URL);

// const API_BASE_URL = 'http://localhost:5000/api';

const CometChatLogin = () => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromUrl = params.get('email');
    if (emailFromUrl) setEmail(emailFromUrl);
  }, [location.search]);

  // STEP 1 → Send OTP
  const sendOtp = async () => {
    if (!email) return toast.warning('Please enter your email');
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/send-otp`, { email });
      toast.success('OTP sent to your email');
      setStep('otp');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'OTP send failed');
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 → Verify OTP → Login to CometChat
  const verifyOtpAndLogin = async () => {
    if (!otp) return toast.warning('Please enter OTP');
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/verify-otp`, {
        email,
        otp
      });
      const { uid, role } = res.data;
      console.log("role->", role);
      // ✅ CometChat LOGIN (UID ONLY)
      if (role === "client") {
        await axios.post(`${API_BASE_URL}/verify-user-last-active`, {
          uid
        });
      }
      await CometChatUIKit.login(uid);
      toast.success('Logged in successfully');
    } catch (err: any) {
      console.log('err->', err);
      toast.error(err?.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step !== 'otp') return;
    setCanResend(false);
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step]);

  const resendOtp = async () => {
    if (!canResend) return;
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/send-otp`, { email });
      toast.success('OTP resent successfully');
      // restart timer
      startResendTimer(); // ✅ restart countdown
      setResendTimer(30);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to resend OTP');
      startResendTimer(); // ✅ restart countdown
    } finally {
      setLoading(false);
    }
  };

  const startResendTimer = () => {
    setCanResend(false);
    setResendTimer(30);

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return interval;
  };

  useEffect(() => {
    if (step !== 'otp') return;
    const interval = startResendTimer();
    return () => clearInterval(interval);
  }, [step]);

  return (
    <div className="cc-login-page">
      {/* LEFT → FORM */}
      <div className="cc-login__form-side">
        <div className="cc-login__form-wrapper">
          <header className="cc-login__logo-container">
            <img
              src={ClientPortal}
              alt="Labor Law PC Logo"
              className="cc-login__logo-centered"
            />
          </header>
          <div className="cc-login__content">
            <h1 className="cc-login__title">Welcome back</h1>
            <p className="cc-login__subtitle">
              Log in to your account to continue chatting.
            </p>
            {/* EMAIL STEP */}
            {step === 'email' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendOtp();
                }}
                className="cc-login__form"
              >
                <div className="cc-login__input-group">
                  <label className="cc-login__label">Email <span className="cc-login__required">*</span></label>
                  <input
                    className="cc-login__input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g. user@email.com"
                    required
                  />
                </div>
                <button
                  className={`cc-login__button ${loading ? 'is-loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? <span className="spinner"></span> : 'Send OTP'}
                </button>
              </form>
            )}
            {/* OTP STEP */}
            {step === 'otp' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  verifyOtpAndLogin();
                }}
                className="cc-login__form"
              >
                <p style={{ fontSize: '14px', color: '#6b7280' }}>
                  OTP sent to <strong>{email}</strong>
                </p>
                <div className="cc-login__input-group">
                  <label className="cc-login__label">Enter OTP <span className="cc-login__required">*</span></label>
                  <OtpInput value={otp} onChange={setOtp} />
                  <div style={{ marginTop: '10px', fontSize: '14px' }}>
                    {canResend ? (
                      <span
                        onClick={resendOtp}
                        style={{ color: '#2563eb', cursor: 'pointer' }}
                      >
                        Resend OTP
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280' }}>
                        Resend OTP in {resendTimer}s
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className={`cc-login__button ${loading ? 'is-loading' : ''}`}
                  disabled={loading}
                >
                  {loading ? <span className="spinner"></span> : 'Verify & Login'}
                </button>
                <button
                  type="button"
                  className="cc-login__change-email"
                  onClick={() => {setStep('email'); setEmail(""); setOtp("");}}
                  aria-label="Use a different email address"
                >
                  Change email address
                </button>
              </form>
            )}
            {/* <div className="cc-login__divider">
              <span>OR</span>
            </div>
            <p className="cc-login__footer">
              Don't have an account?{' '}
              <Link to="/singup" className="cc-login__link">
                Sign up
              </Link>
            </p> */}
          </div>
        </div>
      </div>
      {/* RIGHT → HERO */}
      <div className="cc-login__hero">
        <div className="cc-login__hero-overlay"></div>
        <img
          src={HeroImage}
          alt="Legal Professional"
          className="cc-login__hero-img"
        />
      </div>
    </div>
  );
};

export default CometChatLogin;
